import { cx } from '@linaria/core';
import { useId, useRef } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2ConfigStore, usePM2DisplayContext } from '@pm2/store';
import { usePm2Modal } from '@pm2/ui-lightbox';
import { useIsPrintableContent } from '@provider/print';
import { resets } from '@ui/css';
import { PublisherLogo } from '@ui/kit/icons';
import { printHtmlNodesToJsx } from '@utils/ast';
import { useLoaderState, CSSRefTransition } from '@utils/react';
import { Boundary } from './atoms/Boundary.js';
import { Caption } from './atoms/Caption.js';
import { LinkWrapper } from './atoms/LinkWrapper.js';
import { Scrambled } from './atoms/Scrambled.js';
import { Throbber } from './atoms/Throbber.js';
import { useDimensions } from './hooks/useDimensions.js';
import { useImage } from './hooks/useImage.js';
import { PrintImage } from './image.print.js';
import { contentCls, CROSSFADE_DURATION_MS, imgCls, containerCls, preloaderCls, errorText, publisherLogo, errorMessageCls } from './image.style.js';
import { ImageComponentSelector } from './index.selectors.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

function ImageComponent(props) {
  const modal = usePm2Modal();
  const {
    context = "image-component"
  } = props;
  const id = useId();
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const isPrintContent = useIsPrintableContent();
  const {
    inLightbox
  } = usePM2DisplayContext();
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const captionRef = useRef(null);
  const {
    src,
    state,
    width,
    height
  } = useImage({
    src: props.imageSrc,
    width: props.imageWidth,
    enableScaling: props.enableScaling,
    inLightbox,
    resolveImmediately: inLightbox && context === "image-component",
    containerRef
  });
  const showPreloader = useLoaderState(state === "no-image-loaded", {
    delayMs: 75,
    durationMs: 200
  });
  const dimensions = useDimensions({
    // We only pass a ref to our image once it has been loaded such that the
    // hook does not attempt to use .naturalWidth on an image that has yet to be
    // loaded.
    imageRef: state === "success" ? imageRef : undefined,
    desiredDimensions: {
      width: props.imageWidth ?? width,
      height: props.imageHeight ?? height
    }
  });
  const openInLightbox = () => {
    modal.fromAST([props.node]);
  };
  if (isPrintContent) {
    return /*#__PURE__*/jsx(PrintImage, {
      caption: props.caption,
      displayCaption: props.displayCaption,
      enableScaling: props.enableScaling,
      imageSrc: props.imageSrc,
      imageWidth: props.imageWidth
    });
  }
  if (props.isScrambled) {
    return /*#__PURE__*/jsx(Scrambled, {
      dimensions: dimensions
    });
  }
  const displayCaptionAbove = props.displayCaption === "above" && !inLightbox;
  const displayCaptionBelow = props.displayCaption === "below" || inLightbox;
  return /*#__PURE__*/jsxs("span", {
    ref: containerRef,
    className: cx("gImage", resets.rootCls, containerCls, inLightbox && "inLightbox", displayCaptionAbove && "captionOnTop", displayCaptionBelow && "captionOnBottom", audience === "Grade1To3" && "simple"),
    "data-component-type": ImageComponentSelector,
    style: {
      maxWidth: props.cssMaxWidth
    },
    children: [displayCaptionAbove && /*#__PURE__*/jsx(Caption, {
      position: "above",
      caption: props.caption,
      scrambled: props.isScrambled,
      captionRef: captionRef,
      inLightbox: inLightbox
    }), /*#__PURE__*/jsx(Boundary, {
      captionRef: captionRef,
      enableScaling: props.enableScaling ?? false,
      dimensions: dimensions,
      inLightbox: inLightbox,
      children: /*#__PURE__*/jsx(LinkWrapper, {
        link: props.link,
        state: state,
        openInLightbox: props.enlargeOnClick && !inLightbox ? openInLightbox : undefined,
        children: /*#__PURE__*/jsxs(TransitionGroup, {
          component: null,
          children: [showPreloader.current && /*#__PURE__*/jsx(CSSRefTransition, {
            timeout: CROSSFADE_DURATION_MS,
            classNames: contentCls,
            children: ref => /*#__PURE__*/jsx(Preloader, {
              ref: ref
            })
          }, "preloader"), state === "success" && !showPreloader.current && /*#__PURE__*/jsx(CSSRefTransition, {
            timeout: CROSSFADE_DURATION_MS,
            classNames: contentCls,
            nodeRef: imageRef,
            children: ref => /*#__PURE__*/jsxs(Fragment, {
              children: [/*#__PURE__*/jsx("img", {
                className: cx(contentCls, imgCls),
                ref: ref,
                src: src,
                alt: props.imageAlt ?? "",
                "aria-details": !!props.transcript?.length && !props.isScrambled ? `transcript-${id}` : undefined
              }), !!props.transcript?.length && !props.isScrambled && /*#__PURE__*/jsx("div", {
                className: resets.screenReaderOnlyCls,
                id: `transcript-${id}`,
                "data-disable-polly": true,
                children: printHtmlNodesToJsx(props.transcript)
              })]
            })
          }, "success"), state === "error" && !showPreloader.current && /*#__PURE__*/jsx(CSSRefTransition, {
            timeout: CROSSFADE_DURATION_MS,
            classNames: contentCls,
            children: ref => /*#__PURE__*/jsx(ErrorMessage, {
              ref: ref
            })
          }, "error")]
        })
      })
    }), displayCaptionBelow && /*#__PURE__*/jsx(Caption, {
      position: "below",
      caption: props.caption,
      scrambled: props.isScrambled,
      captionRef: captionRef,
      inLightbox: inLightbox
    })]
  });
}
function Preloader(props) {
  return /*#__PURE__*/jsx("span", {
    ref: props.ref,
    className: cx(contentCls, preloaderCls),
    "aria-label": "Indlæser billede...",
    children: /*#__PURE__*/jsx(Throbber, {})
  });
}
function ErrorMessage(props) {
  return /*#__PURE__*/jsxs("span", {
    ref: props.ref,
    className: cx(contentCls, errorMessageCls),
    children: [/*#__PURE__*/jsx("span", {
      className: errorText,
      children: "Noget gik galt da vi fors\xF8gte at indl\xE6se billedet."
    }), /*#__PURE__*/jsx("div", {
      className: publisherLogo,
      children: /*#__PURE__*/jsx(PublisherLogo, {
        small: true
      })
    })]
  });
}

export { ImageComponent };
//# sourceMappingURL=image.js.map
